import { PageDefinition, PageSuggestion, SuggestionStatus } from '@fullstory/proto/fs/api/pages';
import { ArchiveMultiReply, MLState, ValidationError } from '@fullstory/proto/fs/services/paige';
import { useNotifications } from '@fullstory/react-hooks';
import {
  AppBackendRequestActions,
  AppDispatch,
  fullstorySelectors,
  getFsSpecEnabled,
  PagesRequestActions,
  useFetchFsSpec,
  useFsSpecActions,
} from '@fullstory/redux';
import { useDispatch, useSelector } from 'react-redux';
import {
  PAGE_ARCHIVE_FAILURE,
  PAGE_ARCHIVE_SUCCESS,
  PAGE_SAVE_CONFLICT,
  PAGE_SAVE_FAILURE,
  PAGE_SAVE_SUCCESS,
  PAGE_UNARCHIVE_FAILURE,
  PAGE_UNARCHIVE_SUCCESS,
} from '../constants';
import {
  ArchivePageResponse,
  PageDetail,
  PageFilter,
  PageIssueSilence,
} from '@fullstory/proto/fs/services/dx-objects';
import { SpecTargetObjectType } from '@fullstory/proto/fs/services/search-settings';
import { useIntl } from 'react-intl';
import { useMutation } from '@tanstack/react-query';
import { useRequestMutationOptions } from '@fullstory/requests';
import {
  setSuggestedPageRequest,
  setPagesFromSuggestionsRequest,
  updatePageSuggestionsStatusesRequest,
} from '../requests';
import { int64 } from '@fullstory/proto/google/protobuf';

const { getOrgId } = fullstorySelectors;

export interface IssueSilenceUpdate {
  prev: PageIssueSilence | null | undefined;
  next: PageIssueSilence | null | undefined;
}

export const usePageDefActions = () => {
  const orgId = useSelector(getOrgId);
  const dispatch = useDispatch<AppDispatch>();
  const notifications = useNotifications();
  const intl = useIntl();
  const { updateObjectWithFsSpec, trackSpecMappingUpdate } = useFsSpecActions();
  useFetchFsSpec();
  const fsSpecEnabled = useSelector(getFsSpecEnabled);

  // Save page
  const onSave = async (
    page: PageDefinition,
    onSuccess: () => void,
    // These slugs need to be an exhaustive list of all slugs that should be associated with this element.
    // Not including an existing slug will result in it being removed from the element.
    // Pass null to not affect mappings.
    fsSpecSlugs: string[] | null,
    silence: IssueSilenceUpdate | null,
  ): Promise<ValidationError[] | undefined> => {
    try {
      const rsp = await dispatch(
        PagesRequestActions.setPage({ orgId, page, archived: null }, true),
      );

      if (rsp?.errors.find(error => error.pattern)) {
        notifications.error(
          intl.formatMessage({
            defaultMessage: 'Your page definition conflicts with other pages',
            id: 'gtqXyc',
          }),
          PAGE_SAVE_CONFLICT,
        );
        return rsp.errors;
      } else if (rsp?.errors?.length) {
        notifications.error(
          intl.formatMessage({ defaultMessage: 'Your rules contain an error', id: 'zqEhDa' }),
          PAGE_SAVE_CONFLICT,
        );
        return rsp.errors;
      }

      const { prev, next } = silence || { prev: null, next: null };

      if (next) {
        await dispatch(
          PagesRequestActions.silenceIssue({
            orgId,
            pageId: page.id,
            expiresAt: next.expiresAt,
          }),
        );
      } else if (prev) {
        await dispatch(
          PagesRequestActions.unsilenceIssue({
            orgId,
            pageId: page.id,
            issueSilenceId: prev.id,
          }),
        );
      }

      if (fsSpecEnabled && fsSpecSlugs && rsp.page?.page?.id) {
        const mappings = await updateObjectWithFsSpec(
          `${rsp.page?.page?.id}`,
          SpecTargetObjectType.SPEC_TARGET_OBJECT_TYPE_PAGE,
          fsSpecSlugs,
        );

        mappings.forEach(({ mapping }) => {
          if (mapping?.mapping) trackSpecMappingUpdate(mapping.mapping, 'UPDATE', 'MODAL');
        });
      }

      notifications.success(
        intl.formatMessage({ defaultMessage: 'Successfully saved page', id: '+RCFkF' }),
        PAGE_SAVE_SUCCESS,
      );
      onSuccess();
    } catch (e) {
      notifications.error(
        intl.formatMessage({ defaultMessage: 'Failed to save page', id: 'zNV6EF' }),
        PAGE_SAVE_FAILURE,
      );
    }
    return undefined;
  };

  // Archive page
  const onArchive = async (id: number): Promise<PageDetail | null> => {
    try {
      const rsp = await dispatch(PagesRequestActions.archivePage({ orgId, id }, true));
      notifications.success(
        intl.formatMessage({ defaultMessage: 'Archived Page.', id: 'ydzCxm' }),
        PAGE_ARCHIVE_SUCCESS,
      );

      return rsp?.page || null;
    } catch (e) {
      notifications.error(
        intl.formatMessage({ defaultMessage: 'Failed to archive Page.', id: 'I9drba' }),
        PAGE_ARCHIVE_FAILURE,
      );
    }

    return null;
  };

  // Restore page
  const onRestore = async (
    id: number,
    onConflict: (reply: ArchivePageResponse) => void,
  ): Promise<ArchivePageResponse | null> => {
    try {
      const rsp = await dispatch(PagesRequestActions.archivePage({ orgId, id }, false, true));

      if (rsp.errors.length && rsp.page) {
        onConflict(rsp);
      } else if (rsp.page) {
        notifications.success(
          intl.formatMessage({ defaultMessage: 'Restored Page.', id: 'LOg1B1' }),
          PAGE_UNARCHIVE_SUCCESS,
        );
      }

      return rsp;
    } catch (e) {
      notifications.error(
        intl.formatMessage({ defaultMessage: 'Failed to restore Page.', id: 'VPU/2R' }),
        PAGE_UNARCHIVE_FAILURE,
      );
    }

    return null;
  };

  // Archive multiple
  const onBulkArchive = async (ids: number[]): Promise<ArchiveMultiReply | null> => {
    try {
      const rsp = await dispatch(PagesRequestActions.archivePages({ orgId, ids }));
      notifications.success(
        intl.formatMessage(
          {
            defaultMessage: 'Archived {count, number} Pages.',
            id: 'LBnYH4',
          },
          { count: ids.length },
        ),
      );
      return rsp;
    } catch (e) {
      console.error(e);
      notifications.error(
        intl.formatMessage({ defaultMessage: 'Failed to archive Pages.', id: 'v/PGQl' }),
      );
    }
    return null;
  };

  const archiveAll = async (filters: PageFilter[], query: string, numRows: number) => {
    try {
      await dispatch(
        AppBackendRequestActions.bulkArchivePages({
          orgId,
          filters,
          query,
          lastUpdatedBefore: null,
          volumeThreshold: int64.fromNumber(0),
          referenceThreshold: int64.fromNumber(0),
        }),
      );
      notifications.success(
        intl.formatMessage(
          {
            defaultMessage: 'Archived {count, number} Pages.',
            id: 'LBnYH4',
          },
          { count: numRows },
        ),
      );
    } catch (e) {
      notifications.error(
        intl.formatMessage({
          defaultMessage: 'Failed to archive Pages.',
          id: 'v/PGQl',
        }),
      );
    }
  };

  const onSetMLState = async (mlState: MLState): Promise<void> => {
    try {
      await dispatch(PagesRequestActions.setMLState({ orgId, mlState }));
      notifications.success(
        mlState == MLState.ON
          ? intl.formatMessage({
              defaultMessage: 'Successfully turned on Machine learning pages',
              id: 'iF/7bB',
            })
          : intl.formatMessage({
              defaultMessage: 'Successfully turned off Machine learning pages',
              id: 'w8MqO/',
            }),
      );
    } catch (e) {
      console.error(e);
      notifications.error(
        mlState == MLState.ON
          ? intl.formatMessage({
              defaultMessage: 'Failed to enable Machine learning pages',
              id: 'CDQjKL',
            })
          : intl.formatMessage({
              defaultMessage: 'Failed to disable Machine learning pages',
              id: 't9xNDp',
            }),
      );
    }
  };

  const { mutateAsync: setMutation } = useMutation(
    useRequestMutationOptions(setSuggestedPageRequest, {
      // No default notifications - let callers handle them
    }),
  );

  const rejectSuggestion = async (suggestionId: string, onSuccess: () => void) => {
    const suggestionResp = await dispatch(
      PagesRequestActions.getSuggestedPage({ orgId, suggestionId }),
    );
    const suggestion = suggestionResp.suggestion?.page?.suggestion;
    if (suggestion) {
      const updatedSuggestion: PageSuggestion = {
        ...suggestion,
        status: SuggestionStatus.STATUS_USER_REJECTED,
      };

      setMutation(
        { orgId, suggestion: updatedSuggestion },
        {
          onSuccess: () => {
            notifications.success(
              intl.formatMessage({ defaultMessage: 'Suggestion rejected', id: 'aVGGgE' }),
            );
            onSuccess();
          },
          onError: () => {
            notifications.error(
              intl.formatMessage({ defaultMessage: 'Failed to reject suggestion', id: 'QZjNCB' }),
            );
          },
        },
      );
    }
  };

  const { mutateAsync: setPagesFromSuggestionsMutation } = useMutation(
    useRequestMutationOptions(setPagesFromSuggestionsRequest, {
      onSuccess: (data, variables) => {
        dispatch(PagesRequestActions.getAllPages(orgId, true));

        const totalCount = variables.suggestionIds.length;
        const successCount = data.pages?.length || 0;
        const failedCount = totalCount - successCount;
        const errors = data.errors || [];

        if (errors.length > 0) {
          const patternErrors = errors.filter(error => error.pattern);
          const validationErrors = errors.filter(error => !error.pattern);

          let errorBreakdown = '';

          if (patternErrors.length > 0 && validationErrors.length > 0) {
            // Both types of errors
            errorBreakdown = intl.formatMessage(
              {
                defaultMessage:
                  '{patternCount, number} pattern conflicts, {validationCount, number} validation errors',
                id: '2MMvm5',
              },
              { patternCount: patternErrors.length, validationCount: validationErrors.length },
            );
          } else if (patternErrors.length > 0) {
            // Only pattern conflicts
            errorBreakdown = intl.formatMessage(
              {
                defaultMessage:
                  '{count, number} pattern {count, plural, one {conflict} other {conflicts}}',
                id: 'q9LnZl',
              },
              { count: patternErrors.length },
            );
          } else {
            // Only validation errors
            errorBreakdown = intl.formatMessage(
              {
                defaultMessage:
                  '{count, number} validation {count, plural, one {error} other {errors}}',
                id: 'foUc5v',
              },
              { count: validationErrors.length },
            );
          }

          if (successCount > 0) {
            // Partial success
            const successMessage = intl.formatMessage(
              {
                defaultMessage:
                  'Approved {successCount, number} {successCount, plural, one {suggestion} other {suggestions}}',
                id: 'Wc0zO6',
              },
              { successCount },
            );
            const failureMessage = intl.formatMessage(
              {
                defaultMessage:
                  '{failedCount, number} {failedCount, plural, one {page} other {pages}} failed',
                id: 'LrWfAR',
              },
              { failedCount },
            );

            notifications.error(`${successMessage}, but ${failureMessage}: ${errorBreakdown}`);
          } else {
            // Complete failure
            const failureMessage = intl.formatMessage(
              {
                defaultMessage:
                  '{failedCount, number} {failedCount, plural, one {page} other {pages}} failed to approve',
                id: 'vLsB1d',
              },
              { failedCount },
            );

            notifications.error(`${failureMessage}: ${errorBreakdown}`);
          }
        } else {
          // Complete success
          notifications.success(
            intl.formatMessage(
              {
                defaultMessage:
                  'Successfully approved {count, number} {count, plural, one {suggestion} other {suggestions}}',
                id: 'fCV2rj',
              },
              { count: successCount },
            ),
          );
        }
      },
      onError: () => {
        notifications.error(
          intl.formatMessage({
            defaultMessage: 'Failed to approve suggestions',
            id: 'uLWYj9',
          }),
        );
      },
    }),
  );

  const setPagesFromSuggestions = async (suggestionIds: string[]) =>
    setPagesFromSuggestionsMutation({ orgId, suggestionIds });

  const { mutateAsync: rejectSuggestionsMutation } = useMutation(
    useRequestMutationOptions(updatePageSuggestionsStatusesRequest, {
      onSuccess: data => {
        const count = data.suggestionIds?.length || 0;
        notifications.success(
          intl.formatMessage(
            { defaultMessage: 'Successfully deleted {count, number} suggestions', id: 'JSfXZI' },
            { count },
          ),
        );
      },
      onError: () => {
        notifications.error(
          intl.formatMessage({ defaultMessage: 'Failed to delete suggestions', id: 'otDN/W' }),
        );
      },
    }),
  );

  const rejectSuggestions = async (suggestionIds: string[]) =>
    rejectSuggestionsMutation({
      orgId,
      suggestionIds,
      status: SuggestionStatus.STATUS_USER_REJECTED,
    });

  // Combine multiple page suggestions into a single page suggestion
  const combinePageSuggestions = async (
    pageSuggestions: PageSuggestion[],
    pageName: string,
    description: string,
  ) => {
    if (pageSuggestions.length < 2) {
      return { success: false, error: 'Need at least 2 suggested pages to combine' };
    }

    try {
      const baseSuggestion = pageSuggestions[0];

      if (!baseSuggestion) {
        return { success: false, error: 'No page suggestions provided' };
      }

      // Create combined suggestion
      const allPatterns = pageSuggestions.flatMap(page => page.patterns);
      const allPageArgs = pageSuggestions.flatMap(page => page.thumbnailPageArgs);
      const updatedSuggestion: PageSuggestion = {
        ...baseSuggestion,
        name: pageName.trim(),
        description: description.trim(),
        patterns: allPatterns,
        thumbnailPageArgs: allPageArgs,
      };

      // Save the combined suggestion
      await setMutation({ orgId, suggestion: updatedSuggestion });

      // Bulk reject other suggestions
      const otherSuggestionIds = pageSuggestions.slice(1).map(p => p.id);

      if (otherSuggestionIds.length > 0) {
        await rejectSuggestionsMutation({
          orgId,
          suggestionIds: otherSuggestionIds,
          status: SuggestionStatus.STATUS_USER_REJECTED,
        });
      }

      notifications.success(
        intl.formatMessage(
          {
            defaultMessage: 'Pages successfully combined! A new suggested page has been created.',
            id: 'Wnh0jT',
          },
          {},
        ),
      );

      dispatch(PagesRequestActions.getAllPages(orgId, true));

      return {
        success: true,
        combinedCount: pageSuggestions.length,
        pageName: pageName.trim(),
      };
    } catch (error) {
      notifications.error(
        intl.formatMessage({
          defaultMessage: 'Failed to combine suggested pages',
          id: 'BhjaKq',
        }),
      );

      return { success: false };
    }
  };

  return {
    onSave,
    onArchive,
    onRestore,
    onBulkArchive,
    archiveAll,
    onSetMLState,
    rejectSuggestion,
    setPagesFromSuggestions,
    rejectSuggestions,
    combinePageSuggestions,
  };
};
